'use client'

import { useEffect, useRef } from 'react'
import { ACCEPTED_PHOTO_TYPES } from './PhotoUploader'
import type { UploadedDealFiles } from '@phoenix-web/ux-gql/queries'
import type { AcceptedPhotoTypes } from './PhotoUploader'

const PRELOAD_AHEAD = 2
const PRELOAD_BEHIND = 1

// Must match Next.js default deviceSizes (no custom config in next.config.mjs)
const DEVICE_SIZES = [640, 750, 828, 1080, 1200, 1920, 2048, 3840]

/**
 * Compute the image width the browser will request given sizes="100vw".
 * This replicates the browser's srcset selection: viewport * DPR, then
 * pick the smallest deviceSize that is >= that value.
 */
function getResponsiveWidth(): number {
  const needed = window.innerWidth * window.devicePixelRatio
  return DEVICE_SIZES.find((s) => s >= needed) ?? DEVICE_SIZES[DEVICE_SIZES.length - 1]
}

function getNextImageUrl(src: string, width: number): string {
  return `/_next/image?url=${encodeURIComponent(src)}&w=${width}&q=75`
}

function isImageFile(file: { content_type: string }): boolean {
  return ACCEPTED_PHOTO_TYPES.includes(file.content_type as AcceptedPhotoTypes)
}

export function useImagePreloader(photos: UploadedDealFiles, currentIndex: number, isOpen: boolean) {
  const preloadedUrls = useRef<Set<string>>(new Set())
  const activeImages = useRef<HTMLImageElement[]>([])

  useEffect(() => {
    if (!isOpen || photos.length <= 1) return

    const imageWidth = getResponsiveWidth()
    const indicesToPreload: number[] = []

    for (let i = 1; i <= PRELOAD_AHEAD; i++) {
      indicesToPreload.push((currentIndex + i) % photos.length)
    }
    for (let i = 1; i <= PRELOAD_BEHIND; i++) {
      indicesToPreload.push((currentIndex - i + photos.length) % photos.length)
    }

    const schedule =
      typeof requestIdleCallback === 'function' ? requestIdleCallback : (cb: () => void) => setTimeout(cb, 1)

    const cancel = typeof cancelIdleCallback === 'function' ? cancelIdleCallback : clearTimeout

    const idleIds: number[] = []

    for (const idx of indicesToPreload) {
      const photo = photos[idx]
      if (!isImageFile(photo)) continue

      const url = getNextImageUrl(photo.presigned_url, imageWidth)
      if (preloadedUrls.current.has(url)) continue

      const id = schedule(() => {
        const img = new Image()
        img.src = url
        activeImages.current.push(img)
        preloadedUrls.current.add(url)
      })
      idleIds.push(id as number)
    }

    return () => {
      idleIds.forEach((id) => cancel(id))
    }
  }, [currentIndex, isOpen, photos])

  useEffect(() => {
    if (!isOpen) {
      activeImages.current = []
      preloadedUrls.current.clear()
    }
  }, [isOpen])
}
